import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inmuebleService } from '../services/inmuebleService';
import { operacionTicketService } from '../services/operacionTicketService';
import { Inmueble } from '../types/Inmueble';
import { InmuebleOperacion, Ticket } from '../types/Ticket';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import ActionButton from '../components/common/ActionButton';
import { BsListCheck } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa';
import './InmuebleTickets.css';

const fmt = (v: number | string | undefined) => {
  if (v === undefined || v === null || v === '') return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const fmtPct = (v: number | undefined) => {
  if (v === undefined || v === null) return '-';
  return `${Number(v).toFixed(4)}%`;
};

const fmtDate = (d: string | undefined) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const InmuebleTickets: React.FC = () => {
  const { inmuebleId } = useParams<{ inmuebleId: string }>();
  const navigate = useNavigate();

  const [inmueble, setInmueble] = useState<Inmueble | null>(null);
  const [operaciones, setOperaciones] = useState<InmuebleOperacion[]>([]);
  const [ticketsByOp, setTicketsByOp] = useState<Record<string, Ticket[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!inmuebleId) return;
    load();
  }, [inmuebleId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [inmuebleRes, operacionesRes] = await Promise.all([
        inmuebleService.getById(inmuebleId!),
        operacionTicketService.getOperacionesByInmueble(inmuebleId!),
      ]);
      setInmueble(inmuebleRes.data);
      const ops = operacionesRes.data;
      setOperaciones(ops);

      // Load tickets for all operaciones in parallel
      const ticketResults = await Promise.all(
        ops.map(op =>
          operacionTicketService
            .getTicketsByOperacion(inmuebleId!, op.id!)
            .then(r => ({ id: op.id!, tickets: r.data }))
        )
      );
      const map: Record<string, Ticket[]> = {};
      ticketResults.forEach(({ id, tickets }) => { map[id] = tickets; });
      setTicketsByOp(map);
    } catch {
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const nombreProducto = inmueble?.proyectoNombre || inmueble?.idProyecto || '';
  const nombreInmueble = inmueble?.direccion || inmuebleId;

  return (
    <div className="inmueble-tickets-page">
      <div className="inmueble-tickets-header">
        <ActionButton
          icon={FaArrowLeft}
          text="Volver"
          onClick={() => navigate('/seguimiento-cra')}
          variant="detail"
        />
        <h2>Tickets — {nombreProducto} — {nombreInmueble}</h2>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div className="it-error">{error}</div>}

      {!loading && !error && operaciones.length === 0 && (
        <EmptyState icon={BsListCheck} title="Sin operaciones de tickets" subtitle="Este inmueble no tiene operaciones de tickets generadas." />
      )}

      {!loading && operaciones.map(op => {
        const tickets = ticketsByOp[op.id!] ?? [];
        return (
          <div key={op.id} className="it-operacion-card">
            <div className="it-operacion-header">
              <div className="it-operacion-title">
                <span className="it-op-badge">Operación {op.numOperacion}</span>
                <span className="it-op-count">{op.numTicketsGenerados} tickets</span>
              </div>
              <div className="it-operacion-meta">
                <div className="it-meta-item">
                  <span className="it-meta-label">Fecha emisión</span>
                  <span className="it-meta-value">{fmtDate(op.fechaEmision)}</span>
                </div>
                <div className="it-meta-item">
                  <span className="it-meta-label">Valor inicial</span>
                  <span className="it-meta-value">{fmt(op.valorInicial)}</span>
                </div>
                {op.valorContablePre !== undefined && op.valorContablePre !== null && (
                  <div className="it-meta-item">
                    <span className="it-meta-label">Val. contable anterior</span>
                    <span className="it-meta-value">{fmt(op.valorContablePre)}</span>
                  </div>
                )}
                <div className="it-meta-item">
                  <span className="it-meta-label">Val. contable posterior</span>
                  <span className="it-meta-value">{fmt(op.valorContablePost)}</span>
                </div>
                <div className="it-meta-item">
                  <span className="it-meta-label">Porcentaje</span>
                  <span className="it-meta-value">{fmtPct(op.porcentaje)}</span>
                </div>
              </div>
            </div>

            <table className="it-tickets-table">
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>ID Ticket</th>
                  <th>Inversor</th>
                  <th>Fecha asignación</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.id}>
                    <td>{t.numTicket}</td>
                    <td className="it-ticket-id">{t.idTicket}</td>
                    <td className={t.investorName ? 'it-investor-name' : 'it-investor-empty'}>
                      {t.investorName || '-'}
                    </td>
                    <td className={!t.fechaAsignacion ? 'it-investor-empty' : ''}>
                      {t.fechaAsignacion ? fmtDate(t.fechaAsignacion) : '-'}
                    </td>
                    <td>
                      <span className={`it-estado-badge ${t.active ? 'it-activo' : 'it-inactivo'}`}>
                        {t.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default InmuebleTickets;
